import { computed, inject, Injectable, signal } from '@angular/core';
import { MessagesService } from 'shared/domain';

import { ComponentListWidget, FlowchartWidget, GuidelineWidget } from '@treetop/shared/model';

import { collectRelatedWidgets, remapComponentListIds, remapFlowchartElementIds, stripWidget } from './copy-widget.util';

export const COPIED_WIDGET_STORAGE_KEY = 'tt-copied-widget';
const TTL_MS = 60 * 60 * 1000;

type StoredCopiedWidget = {
  widget: GuidelineWidget;
  relatedWidgets?: GuidelineWidget[];
  expiresAt: number;
};

export type WidgetPasteData = {
  widget: GuidelineWidget;
  relatedWidgets: GuidelineWidget[];
};

@Injectable({ providedIn: 'root' })
export class CopyWidgetService {
  private messagesService = inject(MessagesService);

  private readonly _copiedWidget = signal<GuidelineWidget | null>(this.readFromStorage());
  private readonly _relatedWidgets = signal<GuidelineWidget[]>(this.readRelatedFromStorage());

  readonly hasCopiedWidget = computed(() => this._copiedWidget() !== null && this._copiedWidget()?.widgetType !== 'chapter');
  readonly hasCopiedChapter = computed(() => this._copiedWidget() !== null && this._copiedWidget()?.widgetType === 'chapter');

  private expiryTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.scheduleExpiry();
    this.listenToStorageChanges();
  }

  public copy(widget?: GuidelineWidget, widgetMap?: Record<string, GuidelineWidget>) {
    if (!widget) {
      console.warn('copy() called without widget');
      return;
    }

    let relatedWidgets: GuidelineWidget[] = [];
    if (widget.widgetType === 'component-list' && widgetMap) {
      relatedWidgets = collectRelatedWidgets(widget as ComponentListWidget, widgetMap).map((w) => stripWidget(w));
    }

    const entry: StoredCopiedWidget = {
      widget: stripWidget(widget),
      relatedWidgets: relatedWidgets.length ? relatedWidgets : undefined,
      expiresAt: Date.now() + TTL_MS,
    };

    localStorage.setItem(COPIED_WIDGET_STORAGE_KEY, JSON.stringify(entry));
    this._copiedWidget.set(entry.widget);
    this._relatedWidgets.set(relatedWidgets);

    this.scheduleExpiry();

    this.messagesService.showInfo('GuidelineEditor.Widgets.CopySnackbarMessage', undefined, { duration: 5000 });
  }

  public clear() {
    localStorage.removeItem(COPIED_WIDGET_STORAGE_KEY);
    this._copiedWidget.set(null);
    this._relatedWidgets.set([]);
    this.clearExpiryTimeout();
  }

  private readFromStorage(): GuidelineWidget | null {
    const raw = localStorage.getItem(COPIED_WIDGET_STORAGE_KEY);
    if (!raw) return null;

    try {
      const entry = JSON.parse(raw) as StoredCopiedWidget;

      if (Date.now() > entry.expiresAt) {
        localStorage.removeItem(COPIED_WIDGET_STORAGE_KEY);
        return null;
      }

      return entry.widget;
    } catch {
      localStorage.removeItem(COPIED_WIDGET_STORAGE_KEY);
      return null;
    }
  }

  private readRelatedFromStorage(): GuidelineWidget[] {
    const raw = localStorage.getItem(COPIED_WIDGET_STORAGE_KEY);
    if (!raw) return [];

    try {
      const entry = JSON.parse(raw) as StoredCopiedWidget;
      if (Date.now() > entry.expiresAt) return [];
      return entry.relatedWidgets ?? [];
    } catch {
      return [];
    }
  }

  private scheduleExpiry() {
    this.clearExpiryTimeout();

    const raw = localStorage.getItem(COPIED_WIDGET_STORAGE_KEY);
    if (!raw) {
      return;
    }

    try {
      const entry = JSON.parse(raw) as StoredCopiedWidget;
      const delay = entry.expiresAt - Date.now();

      if (delay <= 0) {
        this.clear();
        return;
      }

      this.expiryTimeoutId = setTimeout(() => {
        this.clear();
      }, delay);
    } catch {
      this.clear();
    }
  }

  private clearExpiryTimeout() {
    if (this.expiryTimeoutId) {
      clearTimeout(this.expiryTimeoutId);
      this.expiryTimeoutId = null;
    }
  }

  private listenToStorageChanges() {
    window.addEventListener('storage', (event) => {
      if (event.key === COPIED_WIDGET_STORAGE_KEY) {
        const value = this.readFromStorage();
        this._copiedWidget.set(value);
        this._relatedWidgets.set(this.readRelatedFromStorage());
        this.scheduleExpiry();
      }
    });
  }

  public getWidgetForPaste(): WidgetPasteData | null {
    const widget = this._copiedWidget();
    if (!widget) return null;

    if (widget.widgetType === 'flowchart') {
      const remapped = remapFlowchartElementIds({ ...widget, uniqueId: crypto.randomUUID() } as FlowchartWidget);
      return { widget: remapped, relatedWidgets: [] };
    }

    if (widget.widgetType === 'component-list') {
      const relatedWidgets = this._relatedWidgets();
      const { widget: remappedWidget, relatedWidgets: remappedRelated } = remapComponentListIds(
        { ...widget, uniqueId: crypto.randomUUID() } as ComponentListWidget,
        relatedWidgets,
      );
      return { widget: remappedWidget, relatedWidgets: remappedRelated };
    }

    return { widget: { ...widget, uniqueId: crypto.randomUUID() }, relatedWidgets: [] };
  }
}
